import React, { useCallback, useMemo } from 'react';
import { string, shape, arrayOf, bool, func, number } from 'prop-types';

import Forms from 'components/form-react-elements';
import Icon from 'components/icon';
import { format, isset } from 'utils';
import {
    AccordionFilters,
    AdvancedFilters,
    CollapsibleFilters
} from './components';
import ActiveFilters from '../ActiveFilters';

const Filters = ({
    activeFilters,
    advancedFilters,
    advancedFiltersTitle,
    clearFiltersButtonTitle,
    clearFilterTitle,
    closeButtonTitle,
    filters,
    filtersModalTitle,
    filtersTitle,
    filtersTitleIsHiddenOnMobile,
    handleAdvancedFiltersSelectChange,
    handleClearFilters,
    handleRadioChange,
    isLoading,
    labelCollapse,
    labelExpand,
    numberFormat,
    onCheckboxChange,
    onResetSelect,
    onCloseButtonClick,
    results,
    showResultsButtonTitle
}) => {
    const formattedResults =
        isset(results) && numberFormat
            ? format.number(results, numberFormat)
            : '';

    const handleCheckboxChange = (event, filterId, controlledFilterId) => {
        onCheckboxChange(event.target.id, filterId, controlledFilterId);
    };

    const isExpanded = useCallback(
        options => options.some(option => option.checked),
        []
    );

    const isAccordionFilter = useMemo(
        () => filters.every(filter => filter.radio && filter.radio.value),
        []
    );

    const renderFilter = item => {
        const { controlledFilterId, id, label, options } = item;

        return (
            <Forms.CheckboxGroup
                disabled={isLoading}
                key={id}
                label={label}
                name={id}
                options={options}
                onChange={event =>
                    handleCheckboxChange(event, id, controlledFilterId)
                }
                checkedLabelIsBold
                isVertical
                labelIsHidden
            />
        );
    };

    if (!filters || !filters.length) {
        return null;
    }

    return (
        <div className="listing-filters">
            <div className="wrapper">
                <div className="listing-filters__header-container">
                    <div className="listing-filters__header">
                        {filtersTitleIsHiddenOnMobile ? (
                            <h2 className="h3">{filtersModalTitle}</h2>
                        ) : (
                            <span className="h3">{filtersModalTitle}</span>
                        )}

                        {closeButtonTitle && (
                            <div className="listing-filters__close-button-container">
                                <button
                                    className="listing-filters__close-button"
                                    type="button"
                                    onClick={onCloseButtonClick}
                                >
                                    {closeButtonTitle}
                                    <Icon glyph="cross" />
                                </button>
                            </div>
                        )}
                    </div>
                    <ActiveFilters
                        activeFilters={activeFilters}
                        activeIndex={0}
                        clearFiltersButtonTitle={clearFiltersButtonTitle}
                        clearFilterTitle={clearFilterTitle}
                        handleClearFilters={handleClearFilters}
                        onCheckboxChange={onCheckboxChange}
                        onResetSelect={onResetSelect}
                    />
                </div>
                {filtersTitle && (
                    <h2
                        className={`listing-filters__title h4 ${
                            filtersTitleIsHiddenOnMobile
                                ? 'listing-filters__title--only-desktop'
                                : ''
                        }`}
                    >
                        {filtersTitle}
                    </h2>
                )}
                <div className="listing-filters__block">
                    {isAccordionFilter ? (
                        <AccordionFilters
                            handleRadioChange={handleRadioChange}
                            filters={filters}
                            labelCollapse={labelCollapse}
                            labelExpand={labelExpand}
                            renderFilter={renderFilter}
                            isLoading={isLoading}
                        />
                    ) : (
                        <React.Fragment>
                            {filters.map(item => {
                                if (item.isCollapsible) {
                                    return (
                                        <CollapsibleFilters
                                            id={`filter-${item.id}`}
                                            labelCollapse={labelCollapse}
                                            labelExpand={labelExpand}
                                            isExpanded={isExpanded(
                                                item.options
                                            )}
                                            key={item.id}
                                            title={item.label}
                                        >
                                            {renderFilter(item)}
                                        </CollapsibleFilters>
                                    );
                                }

                                return (
                                    <div
                                        key={item.id}
                                        className="no-collapsible-filters"
                                    >
                                        {renderFilter(item)}
                                    </div>
                                );
                            })}
                        </React.Fragment>
                    )}
                </div>
                {!!advancedFilters && !!advancedFilters.length && (
                    <AdvancedFilters
                        advancedFilters={advancedFilters}
                        handleAdvancedFiltersSelectChange={
                            handleAdvancedFiltersSelectChange
                        }
                        isLoading={isLoading}
                        title={advancedFiltersTitle}
                    />
                )}

                {showResultsButtonTitle && (
                    <div className="listing-filters__show-results-button-container">
                        <button
                            className="button button--primary listing-filters__show-results-button"
                            disabled={isLoading}
                            onClick={event=>{
                                event.preventDefault();
                                onCloseButtonClick();
                            }}
                        >
                            {showResultsButtonTitle} ({formattedResults})
                            <Icon glyph="arrow-right" />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

Filters.propTypes = {
    activeFilters: arrayOf(shape({})).isRequired,
    advancedFilters: arrayOf(shape({})),
    advancedFiltersTitle: string,
    clearFiltersButtonTitle: string,
    clearFilterTitle: string,
    closeButtonTitle: string,
    filters: arrayOf(shape({})).isRequired,
    filtersModalTitle: string,
    filtersTitle: string,
    filtersTitleIsHiddenOnMobile: bool,
    handleAdvancedFiltersSelectChange: func,
    handleClearFilters: func,
    handleRadioChange: func.isRequired,
    isLoading: bool.isRequired,
    labelCollapse: string.isRequired,
    labelExpand: string.isRequired,
    numberFormat: shape({
        precision: number,
        thousand: string,
        decimal: string
    }),
    onCheckboxChange: func.isRequired,
    onResetSelect: func,
    onCloseButtonClick: func,
    results: number,
    showResultsButtonTitle: string
};

export default Filters;
